import { Navigate, useLocation } from "react-router-dom";
import { useMemo, type ReactNode, useEffect, useState, useSyncExternalStore } from "react";
import { useAuth } from "@boundary/_hooks";
import { AuthService } from "@domain/services/auth.service";
import { isDemoSession } from "@shared/integrations/demoLogin";

interface ProtectedProps {
  children: ReactNode;
}

export function Protected({ children }: ProtectedProps) {
  const location = useLocation();
  const { auth } = useAuth();
  const [reauthTimeout, setReauthTimeout] = useState(false);
  const [initStarted, setInitStarted] = useState(false);
  
  // Реактивно получаем состояние store для проверки initDataRaw
  const store = AuthService.getStore();
  const storeState = useSyncExternalStore(store.subscribe, store.getState);

  const redirectUrl = useMemo(() =>
      location.pathname + location.search, [location.pathname, location.search]);

  // Запускаем инициализацию в фоне, если она еще не началась
  useEffect(() => {
    if (
      auth.state === "anonymous" &&
      !("reauthRequired" in auth ? auth.reauthRequired : false) &&
      (storeState.initDataRaw || isDemoSession()) &&
      !initStarted
    ) {
      console.log("[Protected] Starting background initialization...");
      setInitStarted(true);
      
      // Запускаем инициализацию в фоне, не блокируя UI
      AuthService.init().catch((error) => {
        console.error("[Protected] Initialization failed:", error);
      });
    }
  }, [auth, storeState.initDataRaw, initStarted]);

  // Таймаут для переавторизации (10 секунд)
  useEffect(() => {
    const needsReauth = auth.state === "reauth" || (auth.state === "anonymous" && ("reauthRequired" in auth ? auth.reauthRequired : false));
    if (needsReauth) {
      const timer = setTimeout(() => {
        console.log("[Protected] Reauth timeout, redirecting to /about");
        setReauthTimeout(true);
      }, 10000);

      return () => {
        clearTimeout(timer);
        setReauthTimeout(false);
      };
    } else {
      setReauthTimeout(false);
    }
  }, [auth]);

  // Если переавторизация не удалась за 10 секунд - редиректим
  const needsReauth = auth.state === "reauth" || (auth.state === "anonymous" && ("reauthRequired" in auth ? auth.reauthRequired : false));
  if (needsReauth && reauthTimeout) {
    return (
      <Navigate to="/about" replace state={{ redirect: redirectUrl }}/>
    );
  }

  // Если состояние reauth - показываем контент, переавторизация идет в фоне
  // Не блокируем рендер, чтобы карта и другие компоненты загружались сразу
  if (auth.state === "reauth") {
    console.log("[Protected] Reauth in progress, showing content (reauth in background)");
    return <>{children}</>;
  }

  // Если пользователь анонимный, но требуется переавторизация - показываем контент
  // Переавторизация идет в фоне, не блокируя UI
  if (auth.state === "anonymous" && ("reauthRequired" in auth ? auth.reauthRequired : false)) {
    console.log("[Protected] Reauth required, showing content (reauth in background)");
    return <>{children}</>;
  }

  // Если идет инициализация (есть initDataRaw, но еще anonymous) - не редиректим, авторизация в фоне
  // Показываем контент, авторизация происходит в фоне.
  // На показе подписи мессенджера нет, а вход идёт по пропуску и тоже занимает
  // время: без этого человека успевало выбросить на «О нас» до того, как
  // приложение узнавало, кто он.
  if (
    auth.state === "anonymous" &&
    !("reauthRequired" in auth ? auth.reauthRequired : false) &&
    (storeState.initDataRaw || isDemoSession())
  ) {
    console.log("[Protected] Auth initialization in progress, showing content (auth in background)");
    return <>{children}</>;
  }

  // Если пользователь анонимный и переавторизация не требуется (инициализация завершена)
  if (auth.state === "anonymous" && !("reauthRequired" in auth ? auth.reauthRequired : false)) {
    console.log("[Protected] User anonymous after initialization, redirecting to /about");
    return (
      <Navigate to="/about" replace state={{ redirect: redirectUrl }}/>
    );
  }
  
  // Пользователь авторизован
  if (auth.state === "authenticated") {
    console.log("[Protected] User authenticated, showing protected content");
  }
  
  return <>{children}</>;
}

